import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Popover, useSlotRecipe} from '@chakra-ui/react3'
import {menuSlotRecipe} from '../../theme/slot-recipes/menu'

/**
 * Whether the click opens a new tab or window, or downloads, leaving the
 * current page standing - what `Menu.Item` guarded before the popover. Only a
 * link can, so a modified click on the sign-out button still closes.
 *
 * @param {React.MouseEvent} event
 * @returns {boolean}
 */
const keepsCurrentPage = (event) => {
    const item = event.currentTarget

    if (!item.matches('a[href]')) return false

    return (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        item.hasAttribute('download') ||
        item.getAttribute('target') === '_blank'
    )
}

const ItemEndElement = ({children, showOnHover}) => {
    const recipe = useSlotRecipe({recipe: menuSlotRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    return (
        <Box
            css={styles.itemCommand}
            data-hover-end-element={showOnHover ? '' : undefined}
            aria-hidden="true"
        >
            {children}
        </Box>
    )
}
ItemEndElement.propTypes = {
    children: PropTypes.node,
    showOnHover: PropTypes.bool
}

/**
 * Navigation item used inside the My Stokke account menu.
 *
 * Renders a semantic link or button with the `menu` recipe's item styles, in
 * normal site-navigation tab order. Selecting one closes the panel. Optional
 * leading and trailing elements support the My Stokke dropdown icon layout.
 *
 * @component
 * @param {Object} props
 * @param {React.ElementType} [props.as='button'] - Element or component rendered as the item.
 * @param {React.ReactNode} props.children - Item label/content.
 * @param {React.ReactNode} [props.endElement] - Optional trailing element.
 * @param {React.ReactNode} [props.icon] - Optional leading icon.
 * @param {Function} [props.onClick] - Called when the item is selected.
 * @param {boolean} [props.showEndElementOnHover] - Marks the trailing element as hover-only.
 * @returns {JSX.Element}
 */
export const NavigationMenuItem = ({
    as = 'button',
    children,
    endElement,
    icon,
    onClick,
    showEndElementOnHover,
    ...props
}) => {
    const recipe = useSlotRecipe({recipe: menuSlotRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    return (
        <Popover.Context>
            {(api) => (
                <Box
                    as={as}
                    type={as === 'button' ? 'button' : undefined}
                    css={styles.item}
                    onClick={(event) => {
                        onClick?.(event)
                        if (keepsCurrentPage(event)) return
                        api.setOpen(false)
                    }}
                    {...props}
                    // the global focus ring skips `[class^="leap"]`, so the
                    // recipe's own outline is what shows
                    className="leap-navigation-menu-item"
                >
                    {icon && <Box>{icon}</Box>}
                    {children}
                    {endElement && (
                        <ItemEndElement showOnHover={showEndElementOnHover}>
                            {endElement}
                        </ItemEndElement>
                    )}
                </Box>
            )}
        </Popover.Context>
    )
}
NavigationMenuItem.propTypes = {
    as: PropTypes.elementType,
    children: PropTypes.node,
    endElement: PropTypes.node,
    href: PropTypes.string,
    icon: PropTypes.node,
    onClick: PropTypes.func,
    showEndElementOnHover: PropTypes.bool,
    to: PropTypes.string
}
